import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '@/assets/img/common/warning.png'


const _withScopeId = n => (_pushScopeId("data-v-80da6d60"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "captcha-mesg-box" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "captcha-warning-icon" }, [
    /*#__PURE__*/_createElementVNode("img", { src: _imports_0 })
  ]),
  /*#__PURE__*/_createElementVNode("div", { class: "captcha-mesg" }, [
    /*#__PURE__*/_createElementVNode("b", null, "ボットによるログインの可能性あり"),
    /*#__PURE__*/_createElementVNode("br"),
    /*#__PURE__*/_createTextVNode(" 不正アクセス防止にご協力ください。 ")
  ])
], -1))
const _hoisted_2 = { class: "captcha" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "ログイン前に、以下をチェックしてください", -1))
const _hoisted_4 = ["data-sitekey"]
const _hoisted_5 = ["name"]

import { ref, onMounted, watch, computed } from 'vue';

declare const window: any;

// Define interface for window with grecaptcha
interface WindowWithRecaptcha extends Window {
  grecaptcha?: {
    enterprise: {
      ready: (callback: () => void) => void;
      render: (container: HTMLElement, options: any) => number;
      reset: (widgetId: number) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    }
  }
}

// Type declaration for props
interface ReCaptchaProps {
  action?: string;
  name?: string;
  version?: 'v2' | 'v3';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ReCaptcha',
  props: {
    action: null,
    name: null,
    version: null
  },
  emits: ["token-changed", "recaptcha-loaded"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'token-changed', token: string): void;
  (e: 'recaptcha-loaded'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  action?: string;
  name?: string;
  version?: 'v2' | 'v3';
}



// Default values for optional props
const action = props.action || 'submit';
const name = props.name || 'recaptcha-token';
const version = computed(() => props.version || 'v3');

// Define emits


// References and state
const recaptchaContainer = ref<HTMLElement | null>(null);
const token = ref<string>('');
let recaptchaInstance: number | null | undefined = null;
const loadedScript = ref<HTMLScriptElement | null>(null);
const isLoading = ref<boolean>(false);

// Get reCAPTCHA script URL from environment variables
const recaptchaJsUrl = process.env.RECAPTCHA_URL_JS_RESOURCE;
const siteKey = computed(() => ({
  v2: process.env.RECAPTCHA_V2_SITE_KEY,
  v3: process.env.RECAPTCHA_V3_SITE_KEY,
}));

/**
 * Removes all reCAPTCHA related scripts and resets global state
 */
const cleanupReCaptcha = () => {
  // Remove all reCAPTCHA scripts
  const scripts = document.querySelectorAll('script[src*="recaptcha"]');
  scripts.forEach(script => {
    script.parentNode?.removeChild(script);
  });
  
  // Remove all reCAPTCHA iframes
  const iframes = document.querySelectorAll('iframe[src*="recaptcha"]');
  iframes.forEach(iframe => {
    iframe.parentNode?.removeChild(iframe);
  });

  // Remove all bagdes
  const recaptchaBagdes = document.querySelectorAll('.grecaptcha-badge');
  recaptchaBagdes.forEach(ele => {
    ele.parentNode?.removeChild(ele);
  });
  
  // Clear container if it's v2
  if (recaptchaContainer.value) {
    recaptchaContainer.value.innerHTML = '';
  }
  
  // Reset instance
  recaptchaInstance = null;
  
  // Reset global grecaptcha object
  const win = window as WindowWithRecaptcha;
  if (win.grecaptcha) {
    win.grecaptcha = undefined;
  }
  
  // Reset reference to loaded script
  loadedScript.value = null;
};

/**
 * Load the reCAPTCHA script dynamically
 * @returns Promise that resolves when script is loaded
 */
const loadRecaptchaScript = () => {
  return new Promise<typeof window['grecaptcha']>((resolve, reject) => {
    if (isLoading.value) {
      reject(new Error('Already loading reCAPTCHA'));
      return;
    }
    
    isLoading.value = true;
    
    // Clean up previous reCAPTCHA resources
    cleanupReCaptcha();
    
    const win = window as WindowWithRecaptcha;
    
    // Create and append script element
    const script = document.createElement('script');
    const renderParam = version.value === 'v3' ? siteKey.value.v3 : 'explicit';
    script.src = `${recaptchaJsUrl}&render=${renderParam}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      loadedScript.value = script;
      isLoading.value = false;
      
      // Give a small delay to ensure grecaptcha is initialized
      setTimeout(() => {
        if (win.grecaptcha && win.grecaptcha.enterprise) {
          resolve(win.grecaptcha);
        } else {
          reject(new Error('grecaptcha not initialized after script load'));
        }
      }, 100);
    };
    
    script.onerror = (error) => {
      console.error('Failed to load reCAPTCHA script:', error);
      isLoading.value = false;
      reject(error);
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Initialize reCAPTCHA based on the selected version
 */
const initRecaptcha = async () => {
  try {
    
    const win = window as WindowWithRecaptcha;
    await loadRecaptchaScript();
    
    if (!win.grecaptcha || !win.grecaptcha.enterprise) {
      console.error('reCAPTCHA not loaded properly');
      return;
    }
    
    // Use a small timeout to ensure DOM is ready and grecaptcha is fully initialized
    setTimeout(() => {
      if (version.value === 'v2' && recaptchaContainer.value) {
        // Initialize reCAPTCHA v2 (checkbox)
        win.grecaptcha?.enterprise.ready(() => {
          if (!recaptchaContainer.value) return;
          
          try {
            recaptchaInstance = win.grecaptcha?.enterprise.render(recaptchaContainer.value, {
              sitekey: siteKey.value.v2,
              callback: (response: string) => {
                token.value = response;
                emit('token-changed', response);
              },
              'expired-callback': () => {
                token.value = '';
                emit('token-changed', '');
              }
            });
            emit('recaptcha-loaded');
          } catch (error) {
            console.error('Error rendering reCAPTCHA v2:', error);
          }
        });
      } else if (version.value === 'v3') {
        // Initialize reCAPTCHA v3 (invisible)
        win.grecaptcha?.enterprise.ready(async () => {
          try {
            const tokenValue = await win.grecaptcha?.enterprise.execute(siteKey.value.v3, { action });
            token.value = tokenValue ?? '';
            emit('token-changed', tokenValue ?? '');
            emit('recaptcha-loaded');
          } catch (error) {
            console.error('reCAPTCHA v3 error:', error);
          }
        });
      }
    }, 200);
  } catch (error) {
    console.error('Failed to initialize reCAPTCHA:', error);
  }
};

/**
 * Reset the reCAPTCHA widget
 */
const resetRecaptcha = () => {
  const win = window as WindowWithRecaptcha;
  if (recaptchaInstance !== null && win.grecaptcha && win.grecaptcha.enterprise) {
    if (version.value === 'v2') {
      if (recaptchaInstance) {
        win.grecaptcha.enterprise.reset(recaptchaInstance);
      }
      token.value = '';
      emit('token-changed', '');
    } else if (version.value === 'v3') {
      // For v3, execute again to get a new token
      win.grecaptcha.enterprise.execute(siteKey.value.v3, { action }).then((newToken) => {
        token.value = newToken;
        emit('token-changed', newToken);
      });
    }
  }
};

/**
 * Execute reCAPTCHA v3 manually
 * @returns Promise that resolves with the token
 */
const executeRecaptcha = async (): Promise<string> => {
  const win = window as WindowWithRecaptcha;
  if (version.value === 'v3' && win.grecaptcha && win.grecaptcha.enterprise) {
    try {
      const newToken = await win.grecaptcha.enterprise.execute(siteKey.value.v3, { action });
      token.value = newToken;
      emit('token-changed', newToken);
      return newToken;
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      return '';
    }
  }
  return token.value;
};

// Expose methods to parent component
expose({
  resetRecaptcha,
  executeRecaptcha,
  cleanupReCaptcha,
});

// Watch for changes in token
watch(token, (newValue) => {
  emit('token-changed', newValue);
});

// Watch for changes in version and re-initialize when it changes
watch(() => props.version, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {    
    // Reset token
    token.value = '';
    emit('token-changed', '');
    
    // Re-initialize with new version
    setTimeout(() => {
      initRecaptcha();
    }, 0);
  }
});

// Initialize reCAPTCHA when component is mounted
onMounted(() => {
  initRecaptcha();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(version) == 'v2')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _hoisted_1,
          _createElementVNode("div", _hoisted_2, [
            _hoisted_3,
            _createElementVNode("div", {
              ref_key: "recaptchaContainer",
              ref: recaptchaContainer,
              id: "recaptcha",
              class: "g-recaptcha",
              "data-sitekey": _unref(siteKey)
            }, null, 8, _hoisted_4)
          ])
        ], 64))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("input", {
      type: "hidden",
      name: _unref(name),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((token).value = $event))
    }, null, 8, _hoisted_5), [
      [_vModelText, token.value]
    ])
  ], 64))
}
}

})